import { supabase } from '../lib/supabase';

export const messagingService = {
  // Get all conversations for current user
  async getConversations() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('conversations')?.select(`
        *,
        participants:conversation_participants(
          user_id,
          is_admin,
          joined_at,
          last_read_at,
          user:user_profiles(full_name, role, phone)
        )
      `)?.order('updated_at', { ascending: false });

    if (error) throw error;

    // Get latest message for each conversation separately to ensure proper ordering
    const conversationsWithMessages = await Promise.all(
      (data || [])?.map(async (conv) => {
        // Get the most recent message for this conversation
        const { data: latestMessages } = await supabase
          ?.from('messages')
          ?.select(`
            content, 
            created_at, 
            sender_id, 
            sender:user_profiles(full_name)
          `)
          ?.eq('conversation_id', conv?.id)
          ?.order('created_at', { ascending: false })
          ?.limit(1);

        const latestMsg = latestMessages?.[0];
        const userParticipant = conv?.participants?.find(p => p?.user_id === user?.id);
        
        return {
          id: conv?.id,
          conversationType: conv?.conversation_type,
          title: conv?.title,
          createdBy: conv?.created_by,
          teamId: conv?.team_id,
          createdAt: conv?.created_at,
          updatedAt: conv?.updated_at,
          lastReadAt: userParticipant?.last_read_at,
          lastMessageTimestamp: latestMsg?.created_at || conv?.created_at,
          hasUnreadMessages: latestMsg ? 
            (!userParticipant?.last_read_at || new Date(latestMsg?.created_at) > new Date(userParticipant?.last_read_at)) &&
            latestMsg?.sender_id !== user?.id : false,
          participants: conv?.participants?.map(p => ({
            userId: p?.user_id,
            isAdmin: p?.is_admin,
            joinedAt: p?.joined_at,
            lastReadAt: p?.last_read_at,
            user: {
              fullName: p?.user?.full_name,
              role: p?.user?.role,
              phone: p?.user?.phone
            }
          })),
          latestMessage: latestMsg ? {
            content: latestMsg?.content,
            createdAt: latestMsg?.created_at,
            senderId: latestMsg?.sender_id,
            sender: {
              fullName: latestMsg?.sender?.full_name
            }
          } : null
        };
      })
    );

    // Sort conversations by last message timestamp (most recent first)
    conversationsWithMessages?.sort((a, b) => {
      const timeA = new Date(a?.lastMessageTimestamp)?.getTime();
      const timeB = new Date(b?.lastMessageTimestamp)?.getTime();
      return timeB - timeA; // DESC order (most recent first)
    });

    return conversationsWithMessages;
  },

  // Get messages for a specific conversation
  async getMessages(conversationId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('messages')?.select(`
        *,
        sender:user_profiles(full_name, role)
      `)?.eq('conversation_id', conversationId)?.order('created_at', { ascending: true });

    if (error) throw error;

    // Convert to camelCase for React
    return data?.map(msg => ({
      id: msg?.id,
      conversationId: msg?.conversation_id,
      senderId: msg?.sender_id,
      content: msg?.content,
      messageStatus: msg?.message_status,
      twilioMessageSid: msg?.twilio_message_sid,
      phoneNumber: msg?.phone_number,
      createdAt: msg?.created_at,
      updatedAt: msg?.updated_at,
      sender: {
        fullName: msg?.sender?.full_name,
        role: msg?.sender?.role
      }
    })) || [];
  },

  // Mark conversation as read for current user
  async markConversationAsRead(conversationId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('conversation_participants')
      ?.update({ last_read_at: new Date()?.toISOString() })
      ?.eq('conversation_id', conversationId)
      ?.eq('user_id', user?.id);

    if (error) throw error;
  },

  // Send a new message with enhanced delivery tracking
  async sendMessage(conversationId, content, phoneNumber = null, targetUserId = null) {
  const { data: { user } } = await supabase?.auth?.getUser();
  if (!user) throw new Error('Not authenticated');

  try {
    // ---------------------------------------------------------
    // 1. RESOLVE OR CREATE THE CONVERSATION (1-on-1 fix)
    // ---------------------------------------------------------
    if (!conversationId) {
      if (!targetUserId) {
        throw new Error("targetUserId is required when conversationId is not provided");
      }

      // Check if a conversation already exists between the two users
      const { data: existingConvos } = await supabase?.from("conversations")?.select(`
          id,
          conversation_participants!inner(user_id)
        `)?.contains("conversation_participants", [
          { user_id: user?.id },
          { user_id: targetUserId }
        ]);

      if (existingConvos && existingConvos?.length > 0) {
        conversationId = existingConvos?.[0]?.id;
      } else {
        // Create a new conversation
        const { data: createdConvo, error: convoError } = await supabase?.from("conversations")?.insert({})?.select("id")?.single();
        
        if (convoError) throw convoError;

        conversationId = createdConvo?.id;

        // Add both participants
        await supabase?.from("conversation_participants")?.insert([
          { conversation_id: conversationId, user_id: user?.id },
          { conversation_id: conversationId, user_id: targetUserId }
        ]);
      }
    }

    // ---------------------------------------------------------
    // 2. Validate the user belongs to the conversation
    // ---------------------------------------------------------
    const { data: participant } = await supabase?.from("conversation_participants")?.select("user_id")?.eq("conversation_id", conversationId)?.eq("user_id", user?.id)?.single();

    if (!participant) {
      throw new Error("You are not a participant in this conversation");
    }

    // ---------------------------------------------------------
    // 3. Load all participants
    // ---------------------------------------------------------
    const { data: allParticipants } = await supabase?.from("conversation_participants")?.select(`
        user_id,
        user:user_profiles(full_name, phone)
      `)?.eq("conversation_id", conversationId);

    const recipients = allParticipants?.filter(p => p?.user_id !== user?.id);

    // ---------------------------------------------------------
    // 4. Insert the message (Twilio SMS removed)
    // ---------------------------------------------------------
    const { data, error } = await supabase?.from("messages")?.insert({
        conversation_id: conversationId,
        sender_id: user?.id,
        content,
        message_status: "sent" // Changed from "sending" since we're not using SMS
      })?.select(`
        *,
        sender:user_profiles(full_name, role)
      `)?.single();

    if (error) throw error;

    // ---------------------------------------------------------
    // 5. Broadcast the message
    // ---------------------------------------------------------
    supabase?.channel(`messages:${conversationId}`)?.send({
      type: "broadcast",
      event: "new_message",
      payload: {
        message: {
          id: data?.id,
          conversationId: data?.conversation_id,
          senderId: data?.sender_id,
          content: data?.content,
          messageStatus: data?.message_status,
          phoneNumber: data?.phone_number,
          createdAt: data?.created_at,
          updatedAt: data?.updated_at,
          sender: {
            fullName: data?.sender?.full_name,
            role: data?.sender?.role,
          }
        },
        conversationId,
        senderId: user?.id,
        timestamp: new Date()?.toISOString()
      }
    });

    // ---------------------------------------------------------
    // 6. Mark sender as read
    // ---------------------------------------------------------
    await this.markConversationAsRead(conversationId);

    // Return camelCase result
    return {
      id: data?.id,
      conversationId: data?.conversation_id,
      senderId: data?.sender_id,
      content: data?.content,
      messageStatus: "sent",
      phoneNumber: data?.phone_number,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
      sender: {
        fullName: data?.sender?.full_name,
        role: data?.sender?.role,
      },
      deliveryInfo: {
        recipients: recipients?.length,
        deliveryMethod: 'app_only',
        note: 'SMS messaging has been disabled. Messages are delivered via the application only.'
      }
    };

  } catch (err) {
    console.error("Send message error:", err);
    throw err;
  }
},

  // Create a direct conversation between two users
  async createDirectConversation(participantUserId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      // Check if conversation already exists
      const { data: existingConv } = await supabase?.from('conversation_participants')?.select(`
          conversation_id,
          conversation:conversations(conversation_type)
        `)?.eq('user_id', user?.id);

      // Check if any existing direct conversation includes both users
      for (const conv of existingConv || []) {
        if (conv?.conversation?.conversation_type === 'direct') {
          const { data: otherParticipants } = await supabase?.from('conversation_participants')?.select('user_id')?.eq('conversation_id', conv?.conversation_id);

          const userIds = otherParticipants?.map(p => p?.user_id) || [];
          if (userIds?.includes(participantUserId) && userIds?.length === 2) {
            return { conversationId: conv?.conversation_id, exists: true };
          }
        }
      }

      // Use the new SECURITY DEFINER function to create conversation
      const { data: result, error: rpcError } = await supabase?.rpc(
        'create_conversation_with_participants',
        {
          p_conversation_type: 'direct',
          p_title: null,
          p_team_id: null,
          p_participant_ids: [participantUserId]
        }
      );

      if (rpcError) {
        console.error('RPC error creating conversation:', rpcError);
        throw rpcError;
      }

      return { conversationId: result, exists: false };

    } catch (error) {
      console.error('Error creating direct conversation:', error);
      throw new Error(`Failed to create conversation: ${error?.message}`);
    }
  },

  // Create a team group chat - Enhanced to prevent duplicates and show all members in title
  async createTeamGroupChat(teamId, title) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      // Check if a group chat already exists for this team
      const { data: existingConversations } = await supabase
        ?.from('conversations')
        ?.select('id, title')
        ?.eq('conversation_type', 'group')
        ?.eq('team_id', teamId);

      if (existingConversations && existingConversations?.length > 0) {
        // Return the existing conversation ID instead of creating a new one
        console.log('Team group chat already exists:', existingConversations?.[0]?.id);
        return existingConversations?.[0]?.id;
      }

      // Get current user's profile
      const { data: userProfile } = await supabase?.from('user_profiles')?.select('role, full_name')?.eq('id', user?.id)?.single();

      // Get all team members INCLUDING the creator (all users with this team_id)
      const { data: allTeamMembers } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('team_id', teamId);

      // Extract participant IDs excluding the creator (they're added automatically by RPC)
      const participantIds = allTeamMembers
        ?.filter(member => member?.id !== user?.id)
        ?.map(member => member?.id) || [];

      // Get team details
      const { data: teamData } = await supabase?.from('teams')?.select('team_name')?.eq('id', teamId)?.single();

      // Format title: "Team Name & PCF Admin Name" or "Team Name" for students
      let chatTitle = title;
      if (!title && teamData) {
        if (userProfile?.role === 'pcf_admin') {
          chatTitle = `${teamData?.team_name} & ${userProfile?.full_name}`;
        } else {
          chatTitle = teamData?.team_name;
        }
      }

      // Use the new SECURITY DEFINER function to create conversation
      const { data: conversationId, error: rpcError } = await supabase?.rpc(
        'create_conversation_with_participants',
        {
          p_conversation_type: 'group',
          p_title: chatTitle,
          p_team_id: teamId,
          p_participant_ids: participantIds
        }
      );

      if (rpcError) {
        console.error('RPC error creating team conversation:', rpcError);
        throw rpcError;
      }

      return conversationId;

    } catch (error) {
      console.error('Error creating team group chat:', error);
      throw new Error(`Failed to create team chat: ${error?.message}`);
    }
  },

  /**
   * Get all users for participant selection with role-based filtering
   * Returns individual users based on current user's role
   */
  async getAllUsers() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get current user's profile to determine their role
    const { data: currentUserProfile } = await supabase?.from('user_profiles')?.select('role, team_id')?.eq('id', user?.id)?.single();

    // Get all users with their profiles
    const { data, error } = await // Exclude current user
    supabase?.from('user_profiles')?.select(`
        id,
        full_name,
        email,
        role,
        team_id,
        team_role
      `)?.neq('id', user?.id)?.order('full_name');

    if (error) throw error;

    // Format users with their role information
    return data?.map(user => ({
      id: user?.id,
      fullName: user?.full_name,
      email: user?.email,
      role: user?.role,
      teamId: user?.team_id,
      teamRole: user?.team_role,
      displayRole: this.formatUserRole(user)
    })) || [];
  },

  /**
   * Format user role for display
   */
  formatUserRole(user) {
    const roleMap = {
      'city_official': 'City Official',
      'pcf_admin': 'PCF Admin',
      'student': user?.team_role || 'Team Member'
    };
    return roleMap?.[user?.role] || 'User';
  },

  /**
   * Get teams for group chat creation with role-based filtering
   * Team members can only see their own team
   * Officials and admins can see all teams
   */
  async getTeams() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get current user's profile to determine their role and team
    const { data: currentUserProfile } = await supabase?.from('user_profiles')?.select('role, team_id')?.eq('id', user?.id)?.single();

    let query = supabase?.from('teams')?.select(`
        id,
        team_name,
        team_number,
        mentor,
        point_of_contact
      `)?.order('team_number');

    // If user is a team member (student), only show their own team
    if (currentUserProfile?.role === 'student' && currentUserProfile?.team_id) {
      query = query?.eq('id', currentUserProfile?.team_id);
    }

    const { data, error } = query;

    if (error) throw error;

    return data?.map(team => ({
      id: team?.id,
      teamName: team?.team_name,
      teamNumber: team?.team_number,
      mentor: team?.mentor,
      pointOfContact: team?.point_of_contact
    })) || [];
  },

  /**
   * Get team members for a specific team
   */
  async getTeamMembers(teamId) {
    const { data, error } = await supabase?.from('user_profiles')?.select('id, full_name, email, role, team_role')?.eq('team_id', teamId)?.order('full_name');

    if (error) throw error;

    return data?.map(member => ({
      id: member?.id,
      fullName: member?.full_name,
      email: member?.email,
      role: member?.role,
      teamRole: member?.team_role
    })) || [];
  },

  /**
   * Find or create team group chat for current user's team
   * Returns existing chat if found, creates new one if not
   */
  async getOrCreateTeamGroupChat() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      // Get current user's team_id
      const { data: userProfile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('team_id, full_name')
        ?.eq('id', user?.id)
        ?.single();

      if (profileError) throw profileError;
      if (!userProfile?.team_id) throw new Error('You are not assigned to a team');

      const teamId = userProfile?.team_id;

      // Check if team group chat already exists
      const { data: existingConversations, error: searchError } = await supabase
        ?.from('conversations')
        ?.select('id, title, conversation_type, team_id')
        ?.eq('conversation_type', 'group')
        ?.eq('team_id', teamId);

      if (searchError) throw searchError;

      // If team chat exists, return the first one
      if (existingConversations && existingConversations?.length > 0) {
        console.log('Found existing team group chat:', existingConversations?.[0]?.id);
        return { conversationId: existingConversations?.[0]?.id, created: false };
      }

      // Create new team group chat
      console.log('Creating new team group chat for team:', teamId);

      // Get team details for title
      const { data: teamData, error: teamError } = await supabase
        ?.from('teams')
        ?.select('team_name, team_number')
        ?.eq('id', teamId)
        ?.single();

      if (teamError) throw teamError;

      // Generate system name: "Team {team_name}" or "Team {team_number}"
      const systemGeneratedTitle = teamData?.team_name || `Team ${teamData?.team_number}`;

      // Get all team members
      const { data: allTeamMembers, error: membersError } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('team_id', teamId);

      if (membersError) throw membersError;

      // Extract participant IDs excluding the creator
      const participantIds = allTeamMembers
        ?.filter(member => member?.id !== user?.id)
        ?.map(member => member?.id) || [];

      // Use RPC function to create conversation with all participants
      const { data: conversationId, error: rpcError } = await supabase?.rpc(
        'create_conversation_with_participants',
        {
          p_conversation_type: 'group',
          p_title: systemGeneratedTitle,
          p_team_id: teamId,
          p_participant_ids: participantIds
        }
      );

      if (rpcError) {
        console.error('RPC error creating team conversation:', rpcError);
        throw rpcError;
      }

      console.log('Created new team group chat:', conversationId);
      return { conversationId, created: true };

    } catch (error) {
      console.error('Error in getOrCreateTeamGroupChat:', error);
      throw new Error(`Failed to get or create team chat: ${error?.message}`);
    }
  },

  // Enhanced real-time subscription with better error handling
  subscribeToMessages(conversationId, callback) {
    if (!conversationId || !callback) return null;

    console.log(`Setting up real-time subscription for conversation: ${conversationId}`);

    const channel = supabase?.channel(`messages:${conversationId}`)
      ?.on('postgres_changes', 
          { 
            event: '*', 
            schema: 'public', 
            table: 'messages',
            filter: `conversation_id=eq.${conversationId}`
          },
          async (payload) => {
            try {
              console.log('Real-time message event received:', payload?.eventType, payload);

              // Get full message details when a new message is inserted
              if (payload?.eventType === 'INSERT') {
                const { data: messageWithSender } = await supabase
                  ?.from('messages')
                  ?.select(`
                    *,
                    sender:user_profiles(full_name, role)
                  `)
                  ?.eq('id', payload?.new?.id)
                  ?.single();

                if (messageWithSender) {
                  const formattedMessage = {
                    id: messageWithSender?.id,
                    conversationId: messageWithSender?.conversation_id,
                    senderId: messageWithSender?.sender_id,
                    content: messageWithSender?.content,
                    messageStatus: messageWithSender?.message_status,
                    twilioMessageSid: messageWithSender?.twilio_message_sid,
                    phoneNumber: messageWithSender?.phone_number,
                    createdAt: messageWithSender?.created_at,
                    updatedAt: messageWithSender?.updated_at,
                    sender: {
                      fullName: messageWithSender?.sender?.full_name,
                      role: messageWithSender?.sender?.role
                    }
                  };

                  console.log('Delivering formatted message to callback:', formattedMessage?.id);
                  callback(formattedMessage);
                }
              } else if (payload?.eventType === 'UPDATE') {
                // Handle message status updates (e.g., sent, failed)
                console.log('Message status update:', payload?.new?.message_status);
                callback({ 
                  ...payload?.new, 
                  eventType: 'UPDATE',
                  messageStatus: payload?.new?.message_status 
                });
              }
            } catch (error) {
              console.error('Error processing real-time message:', error);
            }
          })
      ?.on('broadcast', { event: 'new_message' }, (payload) => {
        try {
          console.log('Received broadcast message:', payload);
          
          // Handle broadcast messages (for enhanced delivery)
          if (payload?.payload?.message && payload?.payload?.conversationId === conversationId) {
            callback(payload?.payload?.message);
          }
        } catch (error) {
          console.error('Error processing broadcast message:', error);
        }
      })
      ?.subscribe((status) => {
        console.log(`Message subscription status for ${conversationId}:`, status);
      });

    return channel;
  },

  // Subscribe to conversation updates
  subscribeToConversations(callback) {
    if (!callback) return null;

    console.log('Setting up conversation updates subscription');

    const channel = supabase?.channel('conversations')?.on('postgres_changes', 
          { event: '*', schema: 'public', table: 'conversations' },
          (payload) => {
            try {
              console.log('Conversation update received:', payload);
              callback(payload);
            } catch (error) {
              console.error('Error processing conversation update:', error);
            }
          })?.subscribe((status) => {
            console.log('Conversation subscription status:', status);
          });

    return channel;
  },

  // Subscribe to conversation participant updates
  subscribeToConversationParticipants(callback) {
    if (!callback) return null;

    console.log('Setting up participant updates subscription');

    const channel = supabase?.channel('conversation-participants')?.on('postgres_changes', 
          { event: '*', schema: 'public', table: 'conversation_participants' },
          (payload) => {
            try {
              console.log('Participant update received:', payload);
              callback(payload);
            } catch (error) {
              console.error('Error processing participant update:', error);
            }
          })?.subscribe((status) => {
            console.log('Participant subscription status:', status);
          });

    return channel;
  },

  // Unsubscribe from real-time updates
  unsubscribe(channel) {
    if (channel) {
      console.log('Unsubscribing from real-time channel');
      supabase?.removeChannel(channel);
    }
  },

  // Get unread message count for a user
  async getUnreadMessageCount() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data: conversations } = await supabase
        ?.from('conversations')
        ?.select(`
          id,
          participants:conversation_participants!inner(
            user_id,
            last_read_at
          )
        `)
        ?.eq('participants.user_id', user?.id);

      let totalUnreadCount = 0;

      for (const conv of conversations || []) {
        const userParticipant = conv?.participants?.[0];
        if (userParticipant) {
          const { count } = await supabase
            ?.from('messages')
            ?.select('id', { count: 'exact', head: true })
            ?.eq('conversation_id', conv?.id)
            ?.neq('sender_id', user?.id)
            ?.gt('created_at', userParticipant?.last_read_at || '1970-01-01T00:00:00.000Z');

          totalUnreadCount += count || 0;
        }
      }

      return totalUnreadCount;
    } catch (error) {
      console.error('Error getting unread message count:', error);
      return 0;
    }
  },

  // Send typing indicator (for future implementation)
  async sendTypingIndicator(conversationId, isTyping = true) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return;

    // This could be implemented with a separate typing_indicators table
    // For now, we'll just broadcast it real-time without persisting
    const channel = supabase?.channel(`typing:${conversationId}`);
    
    if (isTyping) {
      channel?.send({
        type: 'broadcast',
        event: 'typing',
        payload: { userId: user?.id, isTyping: true }
      });
    } else {
      channel?.send({
        type: 'broadcast',
        event: 'typing',
        payload: { userId: user?.id, isTyping: false }
      });
    }
  },

  /**
   * Find a user by their full name
   * @param {string} fullName - The user's full name to search for
   * @returns {Promise<{data: Object|null, error: Error|null}>}
   */
  async getUserByName(fullName) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('id, full_name, email, role')?.ilike('full_name', fullName)?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error finding user by name:', error);
      return { data: null, error };
    }
  }
};